import { z } from "zod";
import { type CheckpointTask, CheckpointTaskSchema } from "./checkpoint";
import { type Data, DataSchema } from "./data";

export * from "./checkpoint";
export * from "./data";

export const AssetSchema = z.object({
  id: z.string(),
  name: z.string(),
  mimeType: z.string(),
  url: z.string().optional(), // Remote URL fallback when local file not available
});
export type Asset = z.infer<typeof AssetSchema>;
export interface UrlAsset extends Asset {
  url: string;
}

export const HistoryItemSchema = z.object({
  role: z.enum(["user", "model"]),
  content: z.string(),
});
export type HistoryItem = z.infer<typeof HistoryItemSchema>;

export const DimensionsSchema = z.object({
  width: z.number(),
  height: z.number(),
});
export type Dimensions = z.infer<typeof DimensionsSchema>;

export const AgentContextSchema = z.object({
  init: z.string(),
  render: z.string(),
  helpers: z.record(z.string()).optional(),
  assets: z.array(AssetSchema).optional(),
  dimensions: DimensionsSchema.optional(),
  data: DataSchema.optional(),
});
export type AgentContext = z.infer<typeof AgentContextSchema>;

export const AgentInputSchema = z.object({
  projectId: z.string(),
  message: z.string(),
  userCredits: z.number(),
  checkpoint: z.array(CheckpointTaskSchema).optional(),
  history: z.array(HistoryItemSchema),
  context: AgentContextSchema,
});
export type AgentInput = z.infer<typeof AgentInputSchema>;

export interface AgentStreamMessage {
  explanation?: string;
  duration?: number;
  init?: string;
  render?: string;
  helpers?: Record<string, string>;
  data?: Data;
  assets?: UrlAsset[];
  dimensions?: { width: number; height: number };
  checkpoint?: CheckpointTask[];
}

export interface AgentStreamChunk {
  message?: AgentStreamMessage;
  result?: AgentOutput;
  error?: { type: AgentErrorType; message: string };
}

export interface AgentFlowContext {
  userId: string;
  userEmail: string;
  isAdmin: boolean;
  sendChunk: (chunk: AgentStreamMessage) => void;
}

export type AgentOutput = {
  totalCost?: number; // Only returned to admin users
};

export type AgentErrorType =
  | "UNAUTHENTICATED"
  | "INSUFFICIENT_BALANCE"
  | "INVALID_ARGUMENT"
  | "INTERNAL"
  | "INCOMPLETE_RESPONSE";

export class AgentError extends Error {
  constructor(
    public type: AgentErrorType,
    message: string,
  ) {
    super(message);
  }
}
